// src/pages/admin/Users.tsx
import { useEffect, useMemo, useState, FormEvent } from "react";
import { authFetch, getToken } from "../../lib/auth";

type UserRow = {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  is_admin: boolean;
  created_at: string;
};

const API_BASE = ""; // 배포에선 상대경로 권장

function fmtDate(s: string) {
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  } catch {
    return s;
  }
}

async function parseSafely(res: Response) {
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return await res.json();
  const t = await res.text();
  return { ok: false, message: t || "요청 실패" };
}

function isDeletedUser(r: UserRow) {
  const email = String(r.email || "");
  const name = String(r.name || "");
  return email.includes("@deleted.local") || name === "탈퇴회원";
}

export default function AdminUsers() {
  const [rows, setRows] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 50;

  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const token = getToken();

  const selectedIds = useMemo(
    () =>
      Object.entries(selected)
        .filter(([, v]) => v)
        .map(([k]) => Number(k))
        .filter((n) => Number.isFinite(n) && n > 0),
    [selected]
  );

  const pages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total]);

  async function fetchList(p = 1) {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (term.trim()) qs.set("q", term.trim());
      qs.set("page", String(p));
      qs.set("limit", String(limit));

      const res = await authFetch(`${API_BASE}/api/admin/users?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await parseSafely(res);
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.message || "목록 조회 실패");
      }

      setRows(Array.isArray(data.users) ? data.users : []);
      setTotal(Number(data.total || 0));
      setPage(Number(data.page || p));
      setSelected({});
    } catch (e: any) {
      console.error(e);
      alert(e?.message || "목록 조회 실패");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // “선택 가능” 대상(관리자/이미 탈퇴 처리된 계정은 제외)
  const selectableRowIds = useMemo(() => {
    return rows
      .filter((r) => !r.is_admin && !isDeletedUser(r))
      .map((r) => r.id);
  }, [rows]);

  const allChecked =
    selectableRowIds.length > 0 && selectableRowIds.every((id) => selected[id]);

  const toggleAll = (val: boolean) =>
    setSelected((prev) => {
      const next = { ...prev };
      selectableRowIds.forEach((id) => (next[id] = val));
      // 선택 불가 대상은 체크 해제
      rows.forEach((r) => {
        if (r.is_admin || isDeletedUser(r)) next[r.id] = false;
      });
      return next;
    });

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    fetchList(1);
  };

  async function removeOne(id: number) {
    const r = rows.find((x) => x.id === id);
    if (!r) return;

    if (r.is_admin) {
      alert("관리자 계정은 삭제할 수 없습니다.");
      return;
    }
    if (isDeletedUser(r)) {
      alert("이미 탈퇴 처리된 계정입니다.");
      return;
    }

    if (!confirm("정말 이 사용자를 삭제할까요? (되돌릴 수 없습니다)")) return;

    try {
      const res = await authFetch(`${API_BASE}/api/admin/users/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await parseSafely(res);
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.message || "삭제 실패");
      }
      await fetchList(page);
    } catch (e: any) {
      console.error(e);
      alert(e?.message || "삭제 실패");
    }
  }

  async function removeSelected() {
    const ids = selectedIds;

    if (ids.length === 0) {
      alert("선택된 항목이 없습니다.");
      return;
    }

    if (!confirm(`선택된 ${ids.length}건을 삭제할까요? (되돌릴 수 없습니다)`)) return;

    try {
      // ✅ 일괄 삭제 엔드포인트 사용(서버에 추가한 bulk-delete)
      const res = await authFetch(`${API_BASE}/api/admin/users/bulk-delete`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ids }),
      });

      const data = await parseSafely(res);

      if (!res.ok || data?.ok === false) {
        // 서버에 아직 bulk-delete가 없을 때도 대비(메시지 노출)
        throw new Error(data?.message || "선택 삭제 실패");
      }

      await fetchList(page);
    } catch (e: any) {
      console.error(e);
      alert(e?.message || "선택 삭제 실패");
    }
  }

  return (
    <div className="jp-admin-users space-y-4">
      {/* 상단 헤더 */}
      <div className="jp-admin-page-heading">
        <h1>사용자 관리</h1>
      </div>

      {/* 검색 / 삭제 영역 */}
      <form onSubmit={onSearch} className="jp-admin-filter-bar jp-admin-user-search flex flex-wrap items-center gap-2">
        <input
          className="w-full max-w-md rounded-lg border border-slate-300 px-3 py-2 text-sm"
          aria-label="사용자 검색"
          placeholder="이름·이메일·연락처 검색"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
        <button
          type="submit"
          className="inline-flex items-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:brightness-110 disabled:opacity-60"
          disabled={loading}
        >
          검색
        </button>

        <div className="flex-1" />

        <button
          type="button"
          onClick={() => toggleAll(!allChecked)}
          className="inline-flex items-center rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          disabled={loading || selectableRowIds.length === 0}
          title="관리자/이미 탈퇴 처리된 계정은 선택에서 제외됩니다."
        >
          {allChecked ? "전체 해제" : "전체 선택"}
        </button>

        <button
          type="button"
          onClick={removeSelected}
          className="inline-flex items-center rounded-lg border border-rose-200 px-4 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-60"
          disabled={loading || selectedIds.length === 0}
          title={selectedIds.length ? `${selectedIds.length}건 선택됨` : "선택된 항목이 없습니다"}
        >
          선택 삭제{selectedIds.length ? ` (${selectedIds.length})` : ""}
        </button>
      </form>

      {/* =======================
          모바일: 카드 리스트
          ======================= */}
      <div className="jp-admin-responsive-cards jp-admin-member-cards mt-2">
        {loading && !rows.length && (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500">
            불러오는 중…
          </div>
        )}

        {!loading && !rows.length && (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500">
            결과가 없습니다.
          </div>
        )}

        {rows.map((r) => {
          const deleted = isDeletedUser(r);
          const disabled = r.is_admin || deleted;

          return (
            <div
              key={r.id}
              className="rounded-xl border border-slate-200 bg-white p-4 text-sm shadow-sm"
            >
              {/* 상단: 체크박스 + 상태 + 삭제 버튼 */}
              <div className="flex items-start justify-between gap-3">
                <label className="flex items-center gap-2 text-xs text-slate-500">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 disabled:opacity-50"
                    checked={!!selected[r.id]}
                    disabled={disabled}
                    onChange={(e) =>
                      setSelected((p) => ({
                        ...p,
                        [r.id]: e.target.checked,
                      }))
                    }
                  />
                  <span>ID {r.id}</span>
                </label>

                <div className="flex items-center gap-2">
                  {r.is_admin && (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
                      관리자
                    </span>
                  )}
                  {deleted && (
                    <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] text-rose-700">
                      삭제됨
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={() => removeOne(r.id)}
                    disabled={disabled}
                    className="text-xs text-rose-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                    title={
                      r.is_admin ? "관리자 계정은 삭제할 수 없습니다." : deleted ? "이미 삭제됨" : ""
                    }
                  >
                    삭제
                  </button>
                </div>
              </div>

              {/* 본문 정보 */}
              <div className="mt-3 space-y-1.5">
                <div>
                  <div className="text-xs text-slate-500">이름</div>
                  <div className="text-sm">{r.name || "-"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">이메일</div>
                  <div className="break-all text-sm">{r.email || "-"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">연락처</div>
                  <div className="text-sm">{r.phone || "-"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">가입일</div>
                  <div className="text-sm">{fmtDate(r.created_at)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* =======================
          데스크톱: 테이블
          ======================= */}
      <div className="jp-admin-responsive-table mt-2 rounded-xl border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="w-10 px-3 py-3 text-left">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 disabled:opacity-50"
                  checked={allChecked}
                  disabled={loading || selectableRowIds.length === 0}
                  onChange={(e) => toggleAll(e.target.checked)}
                  title="관리자/이미 탈퇴 처리된 계정은 선택에서 제외됩니다."
                />
              </th>
              <th className="w-16 px-3 py-3 text-left">ID</th>
              <th className="w-40 px-3 py-3 text-left">이름</th>
              <th className="w-[28rem] px-3 py-3 text-left">이메일</th>
              <th className="w-40 px-3 py-3 text-left">연락처</th>
              <th className="w-40 px-3 py-3 text-left">가입일</th>
              <th className="w-24 px-3 py-3 text-center">상태</th>
              <th className="w-20 px-3 py-3 text-center">관리</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const deleted = isDeletedUser(r);
              const disabled = r.is_admin || deleted;

              return (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 disabled:opacity-50"
                      checked={!!selected[r.id]}
                      disabled={disabled}
                      onChange={(e) =>
                        setSelected((p) => ({
                          ...p,
                          [r.id]: e.target.checked,
                        }))
                      }
                    />
                  </td>
                  <td className="px-3 py-2">{r.id}</td>
                  <td className="px-3 py-2">{r.name || "-"}</td>
                  <td className="px-3 py-2 break-all" title={r.email}>
                    {r.email || "-"}
                  </td>
                  <td className="px-3 py-2">{r.phone ?? "-"}</td>
                  <td className="px-3 py-2">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2 text-center">
                    {r.is_admin ? (
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700">
                        관리자
                      </span>
                    ) : deleted ? (
                      <span className="rounded-full bg-rose-50 px-2 py-1 text-xs text-rose-700">
                        삭제됨
                      </span>
                    ) : (
                      <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs text-emerald-700">
                        활성
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => removeOne(r.id)}
                      disabled={disabled}
                      className="inline-flex items-center rounded-lg border border-rose-200 px-3 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                      title={
                        r.is_admin ? "관리자 계정은 삭제할 수 없습니다." : deleted ? "이미 삭제됨" : ""
                      }
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              );
            })}

            {!rows.length && !loading && (
              <tr>
                <td colSpan={8} className="px-3 py-10 text-center text-slate-500">
                  결과가 없습니다.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={8} className="px-3 py-10 text-center text-slate-500">
                  불러오는 중…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      <div className="mt-4 flex items-center justify-end gap-3 text-sm text-slate-600">
        <button
          type="button"
          onClick={() => fetchList(Math.max(1, page - 1))}
          disabled={page <= 1 || loading}
          className="rounded-lg border border-slate-300 px-3 py-1 hover:bg-slate-50 disabled:opacity-60"
        >
          이전
        </button>

        <span>
          {page} / {pages} (총 {total.toLocaleString()}명)
        </span>

        <button
          type="button"
          onClick={() => fetchList(Math.min(pages, page + 1))}
          disabled={page >= pages || loading}
          className="rounded-lg border border-slate-300 px-3 py-1 hover:bg-slate-50 disabled:opacity-60"
        >
          다음
        </button>
      </div>
    </div>
  );
}
